/**
 * 🎯 AK-47 Realtime Hub v3.0 - ЕДИНЫЙ канал для ВСЕХ realtime подписок
 * 
 * Оптимизация v3: вместо 3 каналов используется ОДИН:
 * postgres_changes + broadcast + presence на одном канале
 * 
 * Это снижает WebSocket соединения с 3 до 1 на админа
 * 5000 админов = 5000 соединений (было 15000)
 */

import { useEffect, useCallback, useRef, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useOptimizedHotel } from '@/contexts/OptimizedHotelContext';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import type { RealtimeChannel, RealtimePostgresChangesPayload } from '@supabase/supabase-js';

// Все таблицы которые синхронизируем в реальном времени
export type RealtimeTable = 
  | 'booking_core'
  | 'booking_payments'
  | 'booking_financial'
  | 'rooms'
  | 'room_categories'
  | 'rate_plans'
  | 'seasons'
  | 'category_season_prices'
  | 'rate_exceptions'     // 🎯 Точечные приоритетные цены
  | 'price_matrix_cache'  // 🚀 Materialized Matrix для виджета
  | 'guests'
  | 'booking_services';

// Типы broadcast событий
export type BroadcastEventType = 
  | 'sync'              // Синхронизация данных
  | 'financial_change'  // Финансовые изменения
  | 'sync_progress'     // Прогресс парсинга конкурентов
  | 'notification';     // Push уведомления

// Интерфейс для Presence данных админа
export interface AdminPresence {
  userId: string;
  userName: string;
  userEmail: string;
  userRole: string;
  currentPage: string;
  currentBookingId?: string | null;
  lastActivity: number;
}

// Тип обработчика уведомлений
export type NotificationHandler = (
  table: RealtimeTable,
  eventType: 'INSERT' | 'UPDATE' | 'DELETE',
  payload: any
) => void;

// Тип обработчика прогресса синхронизации
export type SyncProgressHandler = (progressData: any) => void;

// Маппинг таблиц → query keys для инвалидации
// 🔥 AK-47: rate_plans теперь инвалидирует ВСЕ бронирования (для пересчёта цен)
const TABLE_QUERY_KEYS: Record<RealtimeTable, string[]> = {
  booking_core: ['optimized-bookings', 'grid-bookings', 'bookings', 'dashboard'],
  booking_payments: ['optimized-bookings', 'grid-bookings', 'booking-financials', 'payments'],
  booking_financial: ['optimized-bookings', 'grid-bookings', 'booking-financials'],
  rooms: ['rooms', 'optimized-rooms', 'grid-rooms', 'availability'],
  room_categories: ['room-categories', 'categories', 'pricing', 'optimized-bookings', 'grid-bookings'],
  rate_plans: ['rate-plans', 'pricing', 'optimized-bookings', 'grid-bookings', 'bookings'], // 🔥 КРИТИЧНО: пересчёт цен
  seasons: ['seasons', 'pricing', 'optimized-bookings', 'grid-bookings'],
  category_season_prices: ['category-season-prices', 'pricing', 'rates', 'optimized-bookings', 'grid-bookings'],
  rate_exceptions: ['pricing', 'rates', 'grid-rates', 'widget-rates-fast', 'widget-rates-cached'], // 🎯 Точечные цены
  price_matrix_cache: ['widget-rates-fast', 'widget-rates-cached', 'widget-rates-v2', 'pricing'], // 🚀 Виджет бронирования
  guests: ['guests', 'guest-data', 'grid-bookings'],
  booking_services: ['booking-services', 'services', 'optimized-bookings']
};

// Таблицы с hotel_id фильтрацией (напрямую)
// ✅ АК-47: ВСЕ 11 таблиц фильтруются по hotel_id — полная изоляция
const TABLES_WITH_HOTEL_FILTER: RealtimeTable[] = [
  'booking_core',
  'rooms',
  'room_categories',
  'rate_plans',
  'seasons',
  'category_season_prices',
  'rate_exceptions',     // 🎯 Точечные цены (есть hotel_id)
  'price_matrix_cache',  // 🚀 Materialized Matrix
  'guests',
  'booking_services',
  'booking_payments',    // ✅ hotel_id добавлен через миграцию
  'booking_financial'    // ✅ hotel_id добавлен через миграцию
];

// ✅ Пустой массив — все таблицы теперь с фильтрацией
const TABLES_WITHOUT_HOTEL_FILTER: RealtimeTable[] = [];

// Названия таблиц на русском для уведомлений
const TABLE_NAMES_RU: Record<RealtimeTable, string> = {
  booking_core: 'Бронирование',
  booking_payments: 'Платеж',
  booking_financial: 'Финансы',
  rooms: 'Номера',
  room_categories: 'Категории',
  rate_plans: 'Тарифы',
  seasons: 'Сезоны',
  category_season_prices: 'Сезонные цены',
  rate_exceptions: 'Точечные цены',      // 🎯 Приоритетные цены
  price_matrix_cache: 'Кэш цен виджета',  // 🚀 Materialized Matrix
  guests: 'Гости',
  booking_services: 'Услуги'
};

// Типы событий на русском
const EVENT_NAMES_RU: Record<string, string> = {
  INSERT: 'добавлены',
  UPDATE: 'обновлены',
  DELETE: 'удалены'
};

interface RealtimeHubState {
  isConnected: boolean;
  lastUpdate: number;
  subscribedTables: RealtimeTable[];
}

interface UseRealtimeHubOptions {
  /** Показывать toast уведомления */
  showNotifications?: boolean;
  /** Включить логирование */
  debug?: boolean;
}

// ============================
// Глобальные синглтоны для хранения состояния Hub
// v3.0: ОДИН канал вместо трёх
// ============================
let globalMainChannel: RealtimeChannel | null = null;
let globalHotelId: string | null = null;
let subscriberCount = 0;

// Callback registry для уведомлений
const notificationHandlers = new Map<string, NotificationHandler>();
// Callback registry для прогресса синхронизации
const syncProgressHandlers = new Map<string, SyncProgressHandler>();
// Callback registry для broadcast событий
const broadcastHandlers = new Map<string, Map<BroadcastEventType, (payload: any) => void>>();

/**
 * 🔫 AK-47 Realtime Hub v3.0 - единственная точка входа для всех realtime подписок
 * ОДИН канал: postgres_changes + broadcast + presence
 */
export const useRealtimeHub = (options: UseRealtimeHubOptions = {}) => {
  const { showNotifications = false, debug = false } = options;
  
  const { currentHotel } = useOptimizedHotel();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();
  
  const [isConnectedState, setIsConnectedState] = useState(false);
  const lastToastTime = useRef<number>(0);
  const debouncedRefetch = useRef<ReturnType<typeof setTimeout>>();
  const lastBroadcastId = useRef<string>('');
  const isInitialized = useRef(false);

  // Состояние хаба
  const state = useRef<RealtimeHubState>({
    isConnected: false,
    lastUpdate: 0,
    subscribedTables: []
  });

  // ============================
  // PRESENCE STATE (ОТКЛЮЧЕНО — экономия ~9000 realtime req/день)
  // ============================
  const onlineAdmins = useMemo(() => new Map<string, AdminPresence>(), []);

  // Throttled notification
  const showThrottledNotification = useCallback((table: RealtimeTable, eventType: string) => {
    if (!showNotifications) return;
    
    const now = Date.now();
    if (now - lastToastTime.current < 3000) return;
    lastToastTime.current = now;

    const tableName = TABLE_NAMES_RU[table];
    const action = EVENT_NAMES_RU[eventType] || 'изменены';

    toast({
      title: '🔄 Синхронизация',
      description: `${tableName} ${action}`,
      duration: 2000
    });
  }, [toast, showNotifications]);

  // Инвалидация кешей для таблицы
  const invalidateTableQueries = useCallback(async (table: RealtimeTable) => {
    const keys = TABLE_QUERY_KEYS[table];
    if (!keys) return;

    // 🧹 AK-47: Персональные данные гостей кешируются локально (Selectel cache в useOptimizedBookings).
    // Любые обновления booking_core/guests должны сбрасывать этот кеш на ВСЕХ клиентах.
    if (table === 'booking_core' || table === 'guests') {
      try {
        window.dispatchEvent(new CustomEvent('clear-selectel-cache'));
      } catch {
        // no-op (SSR/не браузер)
      }
    }
    
    if (debug) {
      console.log(`[RealtimeHub] Invalidating keys for ${table}:`, keys);
    }
    
    // Форсируем refetch для активных запросов
    for (const key of keys) {
      await queryClient.refetchQueries({ 
        queryKey: [key], 
        type: 'active' 
      });
    }

    // Инвалидируем для следующих запросов
    for (const key of keys) {
      queryClient.invalidateQueries({ queryKey: [key] });
    }
    
    // Всегда обновляем dashboard
    queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    
    state.current.lastUpdate = Date.now();
  }, [queryClient, debug]);

  // Обработчик изменений БД
  const handleDatabaseChange = useCallback((
    table: RealtimeTable,
    payload: RealtimePostgresChangesPayload<any>
  ) => {
    if (debug) {
      console.log(`[RealtimeHub] 📡 ${table}:`, payload.eventType);
    }

    // 🔥 Вызываем зарегистрированные обработчики уведомлений
    notificationHandlers.forEach(handler => {
      try {
        handler(table, payload.eventType as 'INSERT' | 'UPDATE' | 'DELETE', payload);
      } catch (e) {
        console.error('[RealtimeHub] Notification handler error:', e);
      }
    });

    // 🔥 AK-47: Минимальный debounce для быстрой реакции
    if (debouncedRefetch.current) {
      clearTimeout(debouncedRefetch.current);
    }

    debouncedRefetch.current = setTimeout(async () => {
      await invalidateTableQueries(table);
      showThrottledNotification(table, payload.eventType);
    }, 50); // 🔥 50ms вместо 300ms - МГНОВЕННАЯ реакция
  }, [invalidateTableQueries, showThrottledNotification, debug]);

  // ============================
  // BROADCAST FUNCTIONS
  // ============================
  const broadcastChange = useCallback((table: RealtimeTable, eventType: string, entityId?: string) => {
    if (!currentHotel?.id || !globalMainChannel) return;
    
    const broadcastId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    lastBroadcastId.current = broadcastId;
    
    globalMainChannel.send({
      type: 'broadcast',
      event: 'sync',
      payload: { table, eventType, entityId, broadcastId, timestamp: Date.now() }
    });
    
    if (debug) {
      console.log(`[RealtimeHub] 📢 Broadcast:`, { table, eventType, entityId });
    }
  }, [currentHotel?.id, debug]);

  const broadcastBookingChange = useCallback((eventType: string, bookingId: string) => {
    broadcastChange('booking_core', eventType, bookingId);
  }, [broadcastChange]);

  const broadcastFinancialChange = useCallback((eventType: string, bookingId: string) => {
    if (!currentHotel?.id || !globalMainChannel) return;
    
    const broadcastId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    lastBroadcastId.current = broadcastId;
    
    globalMainChannel.send({
      type: 'broadcast',
      event: 'financial_change',
      payload: { eventType, bookingId, broadcastId, timestamp: Date.now() }
    });
    
    if (debug) {
      console.log(`[RealtimeHub] 💰 Financial broadcast:`, { eventType, bookingId });
    }
  }, [currentHotel?.id, debug]);

  const broadcastSyncProgress = useCallback((progressData: any) => {
    if (!currentHotel?.id || !globalMainChannel) return;
    
    globalMainChannel.send({
      type: 'broadcast',
      event: 'sync_progress',
      payload: progressData
    });
    
    if (debug) {
      console.log(`[RealtimeHub] 📊 Sync progress broadcast:`, progressData);
    }
  }, [currentHotel?.id, debug]);

  // ============================
  // PRESENCE FUNCTIONS (ОТКЛЮЧЕНО — экономия heartbeat)
  // ============================
  const trackBookingEdit = useCallback(async (_bookingId: string) => {}, []);
  const untrackBookingEdit = useCallback(async () => {}, []);
  const updateCurrentPage = useCallback(async (_page: string) => {}, []);

  // ============================
  // CALLBACK REGISTRATION
  // ============================
  const registerNotificationHandler = useCallback((handler: NotificationHandler): string => {
    const id = `nh_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    notificationHandlers.set(id, handler);
    if (debug) {
      console.log('[RealtimeHub] Registered notification handler:', id);
    }
    return id;
  }, [debug]);

  const unregisterNotificationHandler = useCallback((id: string) => {
    notificationHandlers.delete(id);
    if (debug) {
      console.log('[RealtimeHub] Unregistered notification handler:', id);
    }
  }, [debug]);

  const registerSyncProgressHandler = useCallback((handler: SyncProgressHandler): string => {
    const id = `sp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    syncProgressHandlers.set(id, handler);
    if (debug) {
      console.log('[RealtimeHub] Registered sync progress handler:', id);
    }
    return id;
  }, [debug]);

  const unregisterSyncProgressHandler = useCallback((id: string) => {
    syncProgressHandlers.delete(id);
    if (debug) {
      console.log('[RealtimeHub] Unregistered sync progress handler:', id);
    }
  }, [debug]);

  // ============================
  // ИНИЦИАЛИЗАЦИЯ HUB — ОДИН КАНАЛ ДЛЯ ВСЕГО
  // ============================
  useEffect(() => {
    if (!currentHotel?.id) return;

    // Если отель изменился, очищаем старый канал
    if (globalHotelId && globalHotelId !== currentHotel.id) {
      if (debug) console.log('[RealtimeHub] Hotel changed, cleaning up...');
      
      if (globalMainChannel) {
        supabase.removeChannel(globalMainChannel);
        globalMainChannel = null;
      }
      globalHotelId = null;
      subscriberCount = 0;
      // presence cleanup removed (disabled)
    }

    subscriberCount++;
    
    // Если канал уже инициализирован для этого отеля - просто увеличиваем счетчик
    if (globalMainChannel && globalHotelId === currentHotel.id) {
      if (debug) console.log('[RealtimeHub] Reusing existing channel, subscribers:', subscriberCount);
      state.current.isConnected = true;
      setIsConnectedState(true);
      isInitialized.current = true;
      return;
    }

    globalHotelId = currentHotel.id;

    console.log(`🎯 [RealtimeHub v3.0] Initializing SINGLE channel for hotel: ${currentHotel.id}`);

    // ============================
    // ЕДИНЫЙ канал: postgres_changes + broadcast + presence
    // ============================
    let channelBuilder = supabase.channel(`realtime_hub_${currentHotel.id}`);

    const allTables: RealtimeTable[] = Object.keys(TABLE_QUERY_KEYS) as RealtimeTable[];
    
    // 1. postgres_changes для всех таблиц
    allTables.forEach(table => {
      const useHotelFilter = TABLES_WITH_HOTEL_FILTER.includes(table);
      
      const filterConfig: { filter?: string } = {};
      if (useHotelFilter) {
        filterConfig.filter = `hotel_id=eq.${currentHotel.id}`;
      }
      
      console.log(`✅ Realtime subscription active: public.${table}.*.${useHotelFilter ? `hotel_id=eq.${currentHotel.id}` : 'ALL'}`);
      
      channelBuilder = channelBuilder.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: table,
          ...filterConfig
        },
        (payload: RealtimePostgresChangesPayload<any>) => {
          if (debug) {
            console.log(`📡 [RealtimeHub] ${table} change detected:`, payload.eventType);
          }
          handleDatabaseChange(table, payload);
        }
      );
    });

    // 2. Broadcast слушатели на том же канале
    channelBuilder = channelBuilder
      // Sync events
      .on('broadcast', { event: 'sync' }, async (payload) => {
        const { table, eventType, broadcastId } = payload.payload || {};
        
        if (broadcastId === lastBroadcastId.current) {
          if (debug) console.log('[RealtimeHub] Ignoring own broadcast');
          return;
        }
        
        if (debug) {
          console.log('[RealtimeHub] 📢 Received sync broadcast:', { table, eventType });
        }
        
        if (table && TABLE_QUERY_KEYS[table as RealtimeTable]) {
          if (debouncedRefetch.current) {
            clearTimeout(debouncedRefetch.current);
          }

          // 🔥 AK-47: Унифицированный debounce 50ms для ВСЕХ операций
          debouncedRefetch.current = setTimeout(async () => {
            await invalidateTableQueries(table as RealtimeTable);
          }, 50);
        }
      })
      // Financial change events (includes timing changes for check_in_time/check_out_time)
      .on('broadcast', { event: 'financial_change' }, async (payload) => {
        const { broadcastId } = payload.payload || {};
        
        if (broadcastId === lastBroadcastId.current) {
          return;
        }
        
        console.log('[RealtimeHub] 💰⏰ Received financial/timing broadcast:', payload.payload);
        
        // 🔥 AK-47: Финансы И времена требуют мгновенной синхронизации
        if (debouncedRefetch.current) {
          clearTimeout(debouncedRefetch.current);
        }

        debouncedRefetch.current = setTimeout(async () => {
          // 🔥 КРИТИЧНО: Инвалидируем ВСЕ связанные кеши, включая booking_core для времени
          await invalidateTableQueries('booking_core');
          await invalidateTableQueries('booking_payments');
          await invalidateTableQueries('booking_financial');
          
          // 🔥 AK-47 FIX: Явно рефетчим optimized-bookings для ШАХМАТКИ
          await queryClient.refetchQueries({ 
            queryKey: ['optimized-bookings'], 
            type: 'active' 
          });
          console.log('[RealtimeHub] ✅ Шахматка синхронизирована (timing/financial change)');
        }, 50); // 🔥 50ms - мгновенная реакция
      })
      // Sync progress events
      .on('broadcast', { event: 'sync_progress' }, (payload) => {
        const progressData = payload.payload;
        if (debug) {
          console.log('[RealtimeHub] 📊 Received sync progress:', progressData);
        }
        
        // Вызываем все зарегистрированные обработчики
        syncProgressHandlers.forEach(handler => {
          try {
            handler(progressData);
          } catch (e) {
            console.error('[RealtimeHub] Sync progress handler error:', e);
          }
        });
      })
      // Notification events
      .on('broadcast', { event: 'notification' }, (payload) => {
        if (debug) {
          console.log('[RealtimeHub] 🔔 Received notification:', payload.payload);
        }
      });

    // 3. Presence ОТКЛЮЧЁН — экономия ~9000 realtime запросов/день

    // 4. ОДИН subscribe для ВСЕГО — postgres_changes + broadcast (без presence)
    globalMainChannel = channelBuilder.subscribe(async (status) => {
      console.log(`🎯 [RealtimeHub v3.1] Channel status: ${status} (postgres_changes + broadcast, NO presence)`);
      state.current.isConnected = status === 'SUBSCRIBED';
      setIsConnectedState(status === 'SUBSCRIBED');
      state.current.subscribedTables = allTables;
    });

    isInitialized.current = true;

    return () => {
      subscriberCount--;
      
      if (debouncedRefetch.current) {
        clearTimeout(debouncedRefetch.current);
      }
      
      // Очищаем канал только когда последний subscriber отписывается
      if (subscriberCount <= 0) {
        console.log('[RealtimeHub v3.0] Last subscriber, cleaning up single channel');
        
        if (globalMainChannel) {
          supabase.removeChannel(globalMainChannel);
          globalMainChannel = null;
        }
        globalHotelId = null;
        subscriberCount = 0;
        notificationHandlers.clear();
        syncProgressHandlers.clear();
      }
    };
  }, [currentHotel?.id, user?.id, handleDatabaseChange, invalidateTableQueries, debug]);

  // Update page on navigation
  useEffect(() => {
    const currentPath = window.location.pathname;
    updateCurrentPage(currentPath);
  }, [updateCurrentPage]);

  return useMemo(() => ({
    // Состояние
    isConnected: isConnectedState,
    subscribedTables: state.current.subscribedTables,
    lastUpdate: state.current.lastUpdate,
    
    // Broadcast функции
    broadcastChange,
    broadcastBookingChange,
    broadcastFinancialChange,
    broadcastSyncProgress,
    
    // Presence данные и функции
    onlineAdmins,
    trackBookingEdit,
    untrackBookingEdit,
    updateCurrentPage,
    
    // Регистрация обработчиков
    registerNotificationHandler,
    unregisterNotificationHandler,
    registerSyncProgressHandler,
    unregisterSyncProgressHandler
  }), [
    isConnectedState,
    broadcastChange, 
    broadcastBookingChange, 
    broadcastFinancialChange,
    broadcastSyncProgress,
    onlineAdmins,
    trackBookingEdit,
    untrackBookingEdit,
    updateCurrentPage,
    registerNotificationHandler,
    unregisterNotificationHandler,
    registerSyncProgressHandler,
    unregisterSyncProgressHandler
  ]);
};

/**
 * Статистика использования Hub (для отладки)
 */
export const getRealtimeHubStats = () => ({
  subscriberCount,
  hotelId: globalHotelId,
  hasChannel: !!globalMainChannel,
  channelCount: globalMainChannel ? 1 : 0, // v3.0: всегда 1 или 0
  tablesCount: Object.keys(TABLE_QUERY_KEYS).length,
  notificationHandlersCount: notificationHandlers.size,
  syncProgressHandlersCount: syncProgressHandlers.size
});
